import { useEffect, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import RootNode from './nodes/RootNode'
import HubNode from './nodes/HubNode'
import QuestionNode from './nodes/QuestionNode'
import AlgorithmNode from './nodes/AlgorithmNode'
import { BEGINNER, MAP_ORDER, MODEL_FAMILIES } from '../data/beginner'
import { PROBLEMS } from '../data/models'

const nodeTypes = {
  root: RootNode,
  hub: HubNode,
  question: QuestionNode,
  algorithm: AlgorithmNode,
}

const MID_X = 300
const RIGHT_X = 660
const ROW_GAP = 128
const ALGO_GAP = 112

export type MapMode = 'problems' | 'models'

interface Props {
  mode: MapMode
  expandedProblemId: string | null // problems mode
  expandedFamily: string | null // models mode
  selectedAlgoId: string | null
  solved: Record<string, string> // problemId -> metric shown on the node
  onProblemClick: (id: string) => void
  onFamilyClick: (name: string) => void
  onAlgoClick: (id: string) => void
  onModeChange: (mode: MapMode) => void
}

const dimEdge = { stroke: '#2b2b36', strokeWidth: 1.6 }
const hotEdge = { stroke: '#2dd4bf', strokeWidth: 2.2 }
const arrow = {
  markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: '#4a4a58' },
}

function questionData(problemId: string, solved: Record<string, string>, hot: boolean, algoId?: string) {
  return {
    index: MAP_ORDER.indexOf(problemId) + 1,
    emoji: BEGINNER[problemId].emoji,
    title: PROBLEMS.find((p) => p.id === problemId)?.title ?? problemId,
    question: BEGINNER[problemId].question,
    solved: problemId in solved,
    solvedMetric: solved[problemId] ?? null,
    hot,
    algoId, // set in models mode: clicking opens this exact model
  }
}

export default function MapView(props: Props) {
  const {
    mode, expandedProblemId, expandedFamily, selectedAlgoId, solved,
    onProblemClick, onFamilyClick, onAlgoClick, onModeChange,
  } = props
  const { fitView } = useReactFlow()

  // camera: zoom to what is being explored, or fit everything
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (mode === 'problems' && expandedProblemId) {
        const problem = PROBLEMS.find((p) => p.id === expandedProblemId)
        if (!problem) return
        fitView({
          nodes: [{ id: problem.id }, ...problem.algorithms.map((a) => ({ id: a.id }))],
          padding: 0.3,
          maxZoom: 1.15,
          duration: 500,
        })
        return
      }
      if (mode === 'models' && expandedFamily) {
        const family = MODEL_FAMILIES.find((f) => f.name === expandedFamily)
        if (!family) return
        fitView({
          nodes: [{ id: `f-${family.name}` }, ...family.members.map((m) => ({ id: `m-${m.algoId}` }))],
          padding: 0.2,
          maxZoom: 1.1,
          duration: 500,
        })
        return
      }
      fitView({ padding: 0.08, maxZoom: 1.0, duration: 500 })
    }, 60)
    return () => window.clearTimeout(t)
  }, [mode, expandedProblemId, expandedFamily, fitView])

  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = []
    const edges: Edge[] = []

    if (mode === 'problems') {
      const centerY = ((MAP_ORDER.length - 1) * ROW_GAP) / 2
      nodes.push(
        { id: 'root', type: 'root', position: { x: 0, y: centerY }, data: {}, draggable: false },
        {
          id: 'models-hub',
          type: 'hub',
          position: { x: 0, y: centerY + 130 },
          data: { hot: false },
          draggable: false,
        },
      )

      MAP_ORDER.forEach((id, i) => {
        const y = i * ROW_GAP
        const expanded = expandedProblemId === id
        nodes.push({
          id,
          type: 'question',
          position: { x: MID_X, y },
          data: questionData(id, solved, expanded),
          draggable: false,
        })
        edges.push({
          id: `e-root-${id}`,
          source: 'root',
          target: id,
          type: 'smoothstep',
          style: expanded ? hotEdge : dimEdge,
          ...arrow,
        })

        if (!expanded) return
        const problem = PROBLEMS.find((p) => p.id === id)
        if (!problem) return
        const baseY = y - ((problem.algorithms.length - 1) * ALGO_GAP) / 2
        problem.algorithms.forEach((algoItem, j) => {
          const selected = selectedAlgoId === algoItem.id
          nodes.push({
            id: algoItem.id,
            type: 'algorithm',
            position: { x: RIGHT_X, y: baseY + j * ALGO_GAP },
            data: {
              name: algoItem.name,
              summary: algoItem.summary,
              metric: `${algoItem.sampleMetric.name} ${algoItem.sampleMetric.value}`,
              selected,
            },
            draggable: false,
          })
          edges.push({
            id: `e-${id}-${algoItem.id}`,
            source: id,
            target: algoItem.id,
            type: 'smoothstep',
            animated: selected,
            style: selected ? { stroke: '#8b5cf6', strokeWidth: 2.2 } : { ...hotEdge, strokeWidth: 1.8 },
            ...arrow,
          })
        })
      })
      return { nodes, edges }
    }

    // ---- models mode ----
    const FAM_GAP = 128
    const centerY = ((MODEL_FAMILIES.length - 1) * FAM_GAP) / 2
    nodes.push(
      { id: 'root', type: 'root', position: { x: 0, y: centerY - 130 }, data: {}, draggable: false },
      {
        id: 'models-hub',
        type: 'hub',
        position: { x: 0, y: centerY },
        data: { hot: true },
        draggable: false,
      },
    )

    MODEL_FAMILIES.forEach((family, i) => {
      const y = i * FAM_GAP
      const expanded = expandedFamily === family.name
      nodes.push({
        id: `f-${family.name}`,
        type: 'algorithm',
        position: { x: MID_X, y },
        data: {
          name: family.name,
          summary: family.summary,
          metric: family.members.length === 1 ? '1 problem' : `${family.members.length} problems`,
          selected: expanded,
        },
        draggable: false,
      })
      edges.push({
        id: `e-hub-${family.name}`,
        source: 'models-hub',
        target: `f-${family.name}`,
        type: 'smoothstep',
        style: expanded ? hotEdge : dimEdge,
        ...arrow,
      })

      if (!expanded) return
      const baseY = y - ((family.members.length - 1) * ROW_GAP) / 2
      family.members.forEach((member, j) => {
        const selected = selectedAlgoId === member.algoId
        nodes.push({
          id: `m-${member.algoId}`,
          type: 'question',
          position: { x: RIGHT_X, y: baseY + j * ROW_GAP },
          data: questionData(member.problemId, solved, selected, member.algoId),
          draggable: false,
        })
        edges.push({
          id: `e-${family.name}-${member.algoId}`,
          source: `f-${family.name}`,
          target: `m-${member.algoId}`,
          type: 'smoothstep',
          animated: selected,
          style: selected ? { stroke: '#8b5cf6', strokeWidth: 2.2 } : { ...hotEdge, strokeWidth: 1.8 },
          ...arrow,
        })
      })
    })
    return { nodes, edges }
  }, [mode, solved, expandedProblemId, expandedFamily, selectedAlgoId])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeClick={(_, node) => {
        if (node.type === 'root') onModeChange('problems')
        else if (node.type === 'hub') onModeChange('models')
        else if (node.type === 'question') {
          const algoId = (node.data as { algoId?: string }).algoId
          algoId ? onAlgoClick(algoId) : onProblemClick(node.id)
        }
        else if (node.type === 'algorithm')
          node.id.startsWith('f-') ? onFamilyClick(node.id.slice(2)) : onAlgoClick(node.id)
      }}
      fitView
      fitViewOptions={{ padding: 0.08, maxZoom: 1.0 }}
      minZoom={0.35}
      maxZoom={1.8}
      colorMode="dark"
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      panOnScroll
    >
      <Background variant={BackgroundVariant.Dots} gap={26} size={1} color="#222230" />
      <Controls showInteractive={false} />
    </ReactFlow>
  )
}
